import { ref, computed, onBeforeUnmount } from 'vue'
import { useToast } from '@/components/ui/toast'
import { useMetrics, MetricEventType } from '@/composables/useMetrics'
import type { PageItem } from './useDocumentState'

export const useDragAndDrop = (
  t: (key: string, params?: Record<string, any>) => string,
  documentState: any
) => {
  const { toast } = useToast()
  const { trackEvent } = useMetrics()
  const {
    organizedDocuments,
    markAsChanged,
    removeDocument,
    insertDocument,
    createDocumentGroup,
    generateDocumentName,
    caseId
  } = documentState

  // Drag state
  const draggedPage = ref<{ docIndex: number; pageIndex: number } | null>(null)
  const draggedPages = ref<{ docIndex: number; pageUuids: string[] } | null>(null)
  const dragOverDocumentGroup = ref<number | null>(null)
  const dragOverTarget = ref<{
    docIndex: number
    pageIndex: number
    dropZone?: 'left' | 'right'
  } | null>(null)
  const dragSourceDocument = ref<number | null>(null)
  const isReorderingWithinDocument = ref(false)

  // UI State Management
  const isDragging = ref(false)
  const dragMode = ref<'reorder' | 'move' | null>(null)

  // Edge-based autoscroll for reaching off-screen document groups
  const autoScrollInterval = ref<number | null>(null)
  const edgeScrollThreshold = 80 // Pixels from edge to trigger scroll
  const scrollSpeed = 10
  let currentMouseY = 0
  let dragOverListener: ((e: DragEvent) => void) | null = null

  /**
   * Set drag mode and state
   */
  const setDragMode = (mode: 'reorder' | 'move' | null) => {
    dragMode.value = mode
    isDragging.value = mode !== null
  }

  /**
   * Clear drag state
   */
  const clearDragState = () => {
    isDragging.value = false
    dragMode.value = null
    draggedPage.value = null
    draggedPages.value = null
    dragOverDocumentGroup.value = null
    dragOverTarget.value = null
    dragSourceDocument.value = null
    isReorderingWithinDocument.value = false
  }

  /**
   * Track mouse position during drag
   */
  const updateMousePosition = (event: DragEvent) => {
    // Only update if we have a valid clientY value (not 0)
    if (event.clientY > 0) {
      currentMouseY = event.clientY
    }
  }

  /**
   * Edge-based autoscroll for dragging to off-screen document groups
   * Only scrolls vertically when near viewport edges
   */
  const startEdgeScroll = () => {
    if (autoScrollInterval.value) return

    // Set up a global dragover listener to track mouse position more reliably
    dragOverListener = (event: DragEvent) => {
      if (event.clientY > 0) {
        currentMouseY = event.clientY
      }
    }
    document.addEventListener('dragover', dragOverListener)

    // Start the scroll interval
    autoScrollInterval.value = window.setInterval(() => {
      if (!isDragging.value) return

      const viewportHeight = window.innerHeight
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop
      const scrollHeight = document.documentElement.scrollHeight

      // Check if near top edge and can scroll up
      if (currentMouseY < edgeScrollThreshold && scrollTop > 0) {
        const distance = edgeScrollThreshold - currentMouseY
        const speed = Math.min(scrollSpeed * (distance / edgeScrollThreshold), scrollSpeed)
        window.scrollBy(0, -speed)
      }
      // Check if near bottom edge and can scroll down
      else if (
        currentMouseY > viewportHeight - edgeScrollThreshold &&
        scrollTop + viewportHeight < scrollHeight
      ) {
        const distance = currentMouseY - (viewportHeight - edgeScrollThreshold)
        const speed = Math.min(scrollSpeed * (distance / edgeScrollThreshold), scrollSpeed)
        window.scrollBy(0, speed)
      }
    }, 16)
  }

  const stopEdgeScroll = () => {
    if (autoScrollInterval.value) {
      clearInterval(autoScrollInterval.value)
      autoScrollInterval.value = null
    }
    if (dragOverListener) {
      document.removeEventListener('dragover', dragOverListener)
      dragOverListener = null
    }
  }

  /**
   * Drag event handlers
   */
  const handleDragStart = (data: { docIndex: number; pageIndex: number }) => {
    draggedPage.value = data
    dragSourceDocument.value = data.docIndex
    draggedPages.value = null
    // Start with 'move' mode to enable drop zones on all documents
    // We'll determine the actual operation (reorder vs move) on drop
    setDragMode('move')
    isReorderingWithinDocument.value = false
    startEdgeScroll()
  }

  const handleDragStartMultiple = (data: { docIndex: number; pageUuids: string[] }) => {
    draggedPages.value = data
    dragSourceDocument.value = data.docIndex
    draggedPage.value = null
    // Start with 'move' mode for multi-page dragging
    setDragMode('move')
    isReorderingWithinDocument.value = false
    startEdgeScroll()
  }

  const handleDragEnd = () => {
    // Clear any pending drag over timeout
    if (dragOverTimeout) {
      clearTimeout(dragOverTimeout)
      dragOverTimeout = null
    }
    stopEdgeScroll()
    clearDragState()
  }

  const handleDragPageMove = (data: {
    sourceDocIndex: number
    sourcePageIndex: number
    targetDocIndex: number
    targetPageIndex: number
  }) => {
    const { sourceDocIndex, sourcePageIndex, targetDocIndex, targetPageIndex } = data
    const sourceDoc = organizedDocuments.value[sourceDocIndex]
    const targetDoc = organizedDocuments.value[targetDocIndex]

    // Safety checks
    if (!sourceDoc || !targetDoc) {
      console.warn('Source or target document not found', { sourceDocIndex, targetDocIndex })
      return
    }

    const reorderWithinSameDocument = sourceDocIndex === targetDocIndex
    // Snapshot only when we need a same-doc diff, and always before reading/moving pages: same index
    // means one shared `pages` array — any splice before this would break "no net reorder" detection.
    const sourcePageOrderBeforeMutation = reorderWithinSameDocument
      ? sourceDoc.pages.map((p: PageItem) => p.uuid).join(',')
      : null

    const page = sourceDoc.pages[sourcePageIndex]
    if (!page) {
      console.warn('Page not found at source index', sourcePageIndex)
      return
    }

    sourceDoc.pages.splice(sourcePageIndex, 1)
    targetDoc.pages.splice(targetPageIndex, 0, page)

    if (reorderWithinSameDocument) {
      const sourcePageOrderAfterMutation = sourceDoc.pages.map((p: PageItem) => p.uuid).join(',')
      if (sourcePageOrderBeforeMutation !== sourcePageOrderAfterMutation) {
        void trackEvent(MetricEventType.PDF_PAGES_REORDERED, {
          document_name: sourceDoc.name,
          case_uuid: caseId.value,
          page_count: sourceDoc.pages.length
        })
      }
    } else {
      // Precision drop across documents (between specific target pages) — semantically a merge,
      // so it must emit PDF_PAGES_MERGED like the whole-group drop path does.
      void trackEvent(MetricEventType.PDF_PAGES_MERGED, {
        page_count: 1,
        source_document_name: sourceDoc.name,
        target_document_name: targetDoc.name,
        case_uuid: caseId.value
      })
    }

    // If source document is now empty and it's different from target, remove it
    if (sourceDoc.pages.length === 0 && sourceDocIndex !== targetDocIndex) {
      removeDocument(sourceDocIndex)
    }

    markAsChanged()
  }

  const handleDragToGroup = (data: {
    sourceDocIndex: number
    sourcePageIndex?: number
    pageUuids?: string[]
    targetDocIndex: number
  }) => {
    const { sourceDocIndex, sourcePageIndex, pageUuids, targetDocIndex } = data
    const sourceDoc = organizedDocuments.value[sourceDocIndex]
    const targetDoc = organizedDocuments.value[targetDocIndex]

    // Safety checks
    if (!sourceDoc || !targetDoc) {
      console.warn('Source or target document not found', { sourceDocIndex, targetDocIndex })
      return
    }

    const crossDocumentDrop = sourceDocIndex !== targetDocIndex
    let mergedPageCount = 0

    if (pageUuids) {
      const pagesToMove = sourceDoc.pages.filter(
        (page: PageItem) => page && page.uuid && pageUuids.includes(page.uuid)
      )
      sourceDoc.pages = sourceDoc.pages.filter(
        (page: PageItem) => page && page.uuid && !pageUuids.includes(page.uuid)
      )
      targetDoc.pages.push(...pagesToMove)
      mergedPageCount = pagesToMove.length

      toast({
        title: t('success'),
        description: t('pagesMoved', { count: pagesToMove.length, targetName: targetDoc.name }),
        variant: 'default'
      })
    } else if (sourcePageIndex !== undefined) {
      const page = sourceDoc.pages[sourcePageIndex]
      if (!page) {
        console.warn('Page not found at index', sourcePageIndex)
        return
      }

      sourceDoc.pages.splice(sourcePageIndex, 1)
      targetDoc.pages.push(page)
      mergedPageCount = 1

      toast({
        title: t('success'),
        description: t('pagesMoved', { count: 1, targetName: targetDoc.name }),
        variant: 'default'
      })
    }

    if (crossDocumentDrop && mergedPageCount > 0) {
      void trackEvent(MetricEventType.PDF_PAGES_MERGED, {
        page_count: mergedPageCount,
        source_document_name: sourceDoc.name,
        target_document_name: targetDoc.name,
        case_uuid: caseId.value
      })
    }

    // If source document is now empty and it's different from target, remove it
    if (sourceDoc.pages.length === 0 && sourceDocIndex !== targetDocIndex) {
      removeDocument(sourceDocIndex)
    }

    markAsChanged()
  }

  // Add a small delay to prevent flickering
  let dragOverTimeout: number | null = null

  const handleDragOverGroup = (docIndex: number) => {
    // Clear any pending timeout
    if (dragOverTimeout) {
      clearTimeout(dragOverTimeout)
    }
    dragOverDocumentGroup.value = docIndex
  }

  const handleDragEnterGroup = (docIndex: number) => {
    // Clear any pending timeout
    if (dragOverTimeout) {
      clearTimeout(dragOverTimeout)
    }
    dragOverDocumentGroup.value = docIndex
  }

  const handleDragLeaveGroup = () => {
    // Delay clearing the drag over state to prevent flickering
    if (dragOverTimeout) {
      clearTimeout(dragOverTimeout)
    }
    dragOverTimeout = window.setTimeout(() => {
      dragOverDocumentGroup.value = null
    }, 50) // Small delay to prevent flickering
  }

  /**
   * Global drag target handlers for precise drop zone targeting
   */
  const handleGlobalDragEnterTarget = (
    docIndex: number,
    pageIndex: number,
    dropZone?: 'left' | 'right'
  ) => {
    dragOverTarget.value = { docIndex, pageIndex, dropZone }
  }

  const handleGlobalDragLeaveTarget = () => {
    dragOverTarget.value = null
  }

  /**
   * Multi-page drag operations
   */
  const startMultiPageDrag = (docIndex: number, pageUuids: string[]) => {
    draggedPages.value = { docIndex, pageUuids }
    draggedPage.value = null
    dragSourceDocument.value = docIndex
    setDragMode('move')
    startEdgeScroll()
  }

  /**
   * Page operations using drag functionality
   */
  const movePagesBetweenDocuments = (
    sourceDocIndex: number,
    pageUuids: string[],
    targetDocIndex: number
  ) => {
    const sourceDoc = organizedDocuments.value[sourceDocIndex]
    const targetDoc = organizedDocuments.value[targetDocIndex]

    if (!sourceDoc || !targetDoc) return

    const pagesToMove = sourceDoc.pages.filter(
      (page: PageItem) => page && pageUuids.includes(page.uuid)
    )
    sourceDoc.pages = sourceDoc.pages.filter(
      (page: PageItem) => page && !pageUuids.includes(page.uuid)
    )
    targetDoc.pages.push(...pagesToMove)

    markAsChanged()

    toast({
      title: t('success'),
      description: t('pagesMoved', { count: pagesToMove.length, targetName: targetDoc.name }),
      variant: 'default'
    })
  }

  const createNewDocumentFromPages = (sourceDocIndex: number, pages: PageItem[]) => {
    const sourceDoc = organizedDocuments.value[sourceDocIndex]
    const newName = generateDocumentName()
    const newDoc = createDocumentGroup(newName, pages)

    sourceDoc.pages = sourceDoc.pages.filter(
      (page: PageItem) => page && !pages.some((p) => p.uuid === page.uuid)
    )
    insertDocument(sourceDocIndex + 1, newDoc)

    markAsChanged()

    toast({
      title: t('success'),
      description: t('documentCreated', { name: newName, count: pages.length }),
      variant: 'default'
    })
  }

  /**
   * Get drag mode indicator text based on drag mode
   */
  const getDragModeText = computed(() => {
    if (!dragMode.value) return ''

    switch (dragMode.value) {
      case 'reorder':
        return 'Reordering pages'
      case 'move':
        return 'Moving pages between documents'
      default:
        return ''
    }
  })

  /**
   * Get drag mode indicator styles based on drag mode
   */
  const getDragModeStyles = computed(() => {
    if (!dragMode.value) return {}

    switch (dragMode.value) {
      case 'reorder':
        return {
          container: 'bg-blue-50 text-blue-700',
          indicator: 'bg-blue-500'
        }
      case 'move':
        return {
          container: 'bg-green-50 text-green-700',
          indicator: 'bg-green-500'
        }
      default:
        return {}
    }
  })

  // Cleanup on unmount
  onBeforeUnmount(() => {
    stopEdgeScroll()
    if (dragOverTimeout) {
      clearTimeout(dragOverTimeout)
      dragOverTimeout = null
    }
  })

  return {
    // State
    draggedPage,
    draggedPages,
    dragOverDocumentGroup,
    dragOverTarget,
    dragSourceDocument,
    isDragging,
    dragMode,

    // Methods
    setDragMode,
    clearDragState,
    updateMousePosition,

    // Drag handlers
    handleGlobalDragStart: handleDragStart,
    handleGlobalDragStartMultiple: handleDragStartMultiple,
    handleGlobalDragEnd: handleDragEnd,
    handleGlobalDragPageMove: handleDragPageMove,
    handleGlobalDragToGroup: handleDragToGroup,
    handleGlobalDragOverGroup: handleDragOverGroup,
    handleGlobalDragEnterGroup: handleDragEnterGroup,
    handleGlobalDragLeaveGroup: handleDragLeaveGroup,
    handleGlobalDragEnterTarget,
    handleGlobalDragLeaveTarget,

    // Multi-page operations
    startMultiPageDrag,
    movePagesBetweenDocuments,
    createNewDocumentFromPages,

    // Computed
    getDragModeText,
    getDragModeStyles
  }
}
